import type { WaitingListEntry } from '@/services/waitingListService'

export const WAITLIST_EXPIRED_NOTICE_MS = 7 * 24 * 60 * 60 * 1000

const TERMINAL_ACTIONS = new Set(['register', 'reject', 'remove'])

export function isWaitlistNotified(entry: WaitingListEntry): boolean {
  const notified = entry.notified as unknown
  return notified === true || notified === 1 || notified === '1' || String(notified).toLowerCase() === 'true'
}

export function isWaitlistDeleted(entry: WaitingListEntry): boolean {
  return entry.deleted === true || String(entry.deleted) === 'true'
}

/** Clock time on the string, so `2026-01-15T15:07:00` stays 3:07 PM local. */
export function parseWaitlistDate(value?: string | null): Date | null {
  if (!value) return null
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/)
  if (match) {
    const [, year, month, day, hour = '0', minute = '0', second = '0'] = match
    return new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second))
  }
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

export function isActiveWaitlistEntry(entry: WaitingListEntry): boolean {
  if (isWaitlistDeleted(entry)) return false
  return !TERMINAL_ACTIONS.has((entry.action || '').toLowerCase())
}

/**
 * Real deadline on an active row. Joining the list writes expDate equal to
 * timeStamp, which is not a deadline, so expDate must be later than timeStamp.
 */
export function notifiedWaitlistExpiration(entry: WaitingListEntry): Date | null {
  if (!isActiveWaitlistEntry(entry)) return null

  const expiration = parseWaitlistDate(entry.expDate)
  if (!expiration) return null

  const joined = parseWaitlistDate(entry.timeStamp)
  if (joined && expiration.getTime() <= joined.getTime()) return null
  return expiration
}

/**
 * Show a row once a slot was announced (notified) or it has a real deadline.
 * A deadline stays visible for a week after it passes.
 */
export function isVisibleWaitlistOffer(entry: WaitingListEntry, now = new Date()): boolean {
  if (!isActiveWaitlistEntry(entry)) return false

  const expiration = notifiedWaitlistExpiration(entry)
  if (!expiration) return isWaitlistNotified(entry)
  if (expiration.getTime() > now.getTime()) return true
  return expiration.getTime() > now.getTime() - WAITLIST_EXPIRED_NOTICE_MS
}

export type WaitlistCountdown = {
  expired: boolean
  hours: number
  minutes: number
  underOneMinute: boolean
}

export function getWaitlistCountdown(expiration: Date, now = new Date()): WaitlistCountdown {
  const remainingMs = expiration.getTime() - now.getTime()
  if (remainingMs <= 0) {
    return { expired: true, hours: 0, minutes: 0, underOneMinute: false }
  }

  const hours = Math.floor(remainingMs / 3_600_000)
  const minutes = Math.floor((remainingMs % 3_600_000) / 60_000)
  return {
    expired: false,
    hours,
    minutes,
    underOneMinute: remainingMs < 60_000,
  }
}

type SchedulePoint = {
  startDate?: string
  endDate?: string
  locationName?: string
}

function wallClock(value?: string): Date | null {
  if (!value) return null
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/)
  if (!match) return parseWaitlistDate(value)
  const [, year, month, day, hour = '0', minute = '0'] = match
  return new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute))
}

function sameCalendarDay(left: Date, right: Date): boolean {
  return left.getFullYear() === right.getFullYear()
    && left.getMonth() === right.getMonth()
    && left.getDate() === right.getDate()
}

/** Same schedule sentence as the waiting-list slot email. */
export function formatWaitlistSchedule(
  schedule: SchedulePoint[] | undefined,
  locale: string,
): { schedule: string | null; location: string | null } {
  const points = (schedule ?? [])
    .map(point => ({
      start: wallClock(point.startDate),
      end: wallClock(point.endDate),
      locationName: point.locationName?.trim() || '',
    }))
    .filter(point => point.start)

  if (points.length === 0) return { schedule: null, location: null }

  points.sort((a, b) => a.start!.getTime() - b.start!.getTime())
  const start = points[0].start!
  const end = points.reduce((latest, point) => {
    const candidate = point.end ?? point.start!
    return candidate.getTime() > latest.getTime() ? candidate : latest
  }, start)
  const location = points.find(point => point.locationName)?.locationName || null

  const dateLabel = (date: Date) => date.toLocaleDateString(locale, {
    month: 'long',
    day: '2-digit',
    year: 'numeric',
  })
  const timeLabel = (date: Date) => date.toLocaleTimeString(locale, {
    hour: 'numeric',
    minute: '2-digit',
  })

  const scheduleLabel = sameCalendarDay(start, end)
    ? `${dateLabel(start)} from ${timeLabel(start)} to ${timeLabel(end)}`
    : `${dateLabel(start)} ${timeLabel(start)} to ${dateLabel(end)} ${timeLabel(end)}`

  return { schedule: scheduleLabel, location }
}
